import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpContext } from "@angular/common/http";
import { Observable, tap } from "rxjs";
import { MITPService } from "./mitp.service";
import { SKIP_ERROR_TOAST, SKIP_LOADING } from "./http-context-tokens";
import { AnsweredCall, RingingCall } from "../interfaces/remote-guidance";

/** Remote guidance calls from the smart glasses: the backend places and closes them, LiveKit carries them. */
@Injectable({
	providedIn: "root",
})
export class RemoteGuidanceService {
	private httpClient = inject(HttpClient);
	private mitp = inject(MITPService);

	/** The call the expert has just answered, read by the session page to join its room. */
	private answered: AnsweredCall | null = null;

	/** Polled in the background: no loading bar, and errors are the caller's to handle. */
	ringing(): Observable<RingingCall[]> {
		return this.httpClient.get<RingingCall[]>(`${this.mitp.RemoteGuidance()}/sessions/ringing`, {
			context: new HttpContext().set(SKIP_LOADING, true).set(SKIP_ERROR_TOAST, true),
		});
	}

	/** Fails with a 409 when another expert answered first; the caller says so itself. */
	accept(id: string): Observable<AnsweredCall> {
		return this.httpClient
			.post<AnsweredCall>(`${this.mitp.RemoteGuidance()}/sessions/${id}/accept`, null, {
				context: new HttpContext().set(SKIP_ERROR_TOAST, true),
			})
			.pipe(tap((call) => (this.answered = call)));
	}

	end(id: string): Observable<void> {
		return this.httpClient.post<void>(`${this.mitp.RemoteGuidance()}/sessions/${id}/end`, null, {
			context: new HttpContext().set(SKIP_LOADING, true).set(SKIP_ERROR_TOAST, true),
		});
	}

	/** The answered call with this id, once: a reload of the session page cannot join again. */
	takeAnswered(id: string): AnsweredCall | null {
		const call = this.answered?.id === id ? this.answered : null;
		this.answered = null;
		return call;
	}
}
